import { useState, useMemo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Paper, Box, Typography, CircularProgress } from '@gapartners/ui';
import { MetricsFilterInputs } from '../common/MetricsFilterInputs';
import { SectionTitleWithInfo } from '../common/SectionTitleWithInfo';
import { ExportCsvButton } from '../common/ExportCsvButton';
import { MetricCard } from '../common/MetricCard';
import {
  toMetricsQueryParams,
  type MetricsFilterParams,
} from '@/utils/metricsParams';
import type {
  MetricGrouping,
  MetricsOutputDTO,
  MetricItemDTO,
} from '@/types/agentEvents';

/**
 * Configuration for a reusable metrics section component.
 * Reduces code duplication across all metric section components.
 */
export interface MetricsSectionConfig {
  /** Translation keys for section title and description */
  titleKey: string;
  descriptionKey: string;

  /** The metric name for CSV export (e.g., 'occupancy', 'adherence') */
  metricName: string;

  /** Default parameters for this metric query */
  defaultParams: MetricsFilterParams;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useQuery: (params?: any) => {
    data?: MetricsOutputDTO;
    isLoading: boolean;
    isFetching: boolean;
    error?: unknown;
    refetch: () => void;
  };

  groupingsOptions?: MetricGrouping[];

  refreshLabelKey?: string;

  extraParams?: Record<string, unknown>;

  extraControls?: (
    params: MetricsFilterParams,
    setParams: (p: MetricsFilterParams) => void
  ) => ReactNode;

  renderData?: (props: {
    data: MetricItemDTO[];
    params: MetricsFilterParams;
    metricValue: (v: number | string) => number;
    groupingKey: string;
  }) => ReactNode;
}

export function MetricsSection({
  titleKey,
  descriptionKey,
  metricName,
  defaultParams,
  useQuery,
  groupingsOptions,
  refreshLabelKey,
  extraParams = {},
  extraControls,
  renderData,
}: MetricsSectionConfig) {
  const { t } = useTranslation();
  const [params, setParams] = useState<MetricsFilterParams>(defaultParams);

  // Build query params from filter params + extra params
  const queryParams = useMemo(() => {
    const baseParams = toMetricsQueryParams(params);
    return { ...baseParams, ...extraParams };
  }, [params, extraParams]);

  // Execute the query
  const { data, isLoading, isFetching, error, refetch } = useQuery(queryParams);

  // Helper to extract numeric value from metrics
  const metricValue = (value: number | string): number => {
    if (typeof value === 'number') return value;
    const n = parseFloat(String(value).replace(/%/g, ''));
    return Number.isFinite(n) ? n : 0;
  };

  // Extract grouping key for dimension labels
  const groupingKey = (params.groupings?.[0] as string) ?? 'AGENT';
  const csvGroupingKey =
    groupingsOptions && groupingsOptions.length > 0 ? groupingKey : 'QUEUE';

  // Render error UI if error state exists
  const errorUI = error ? (
    <Box role="alert" sx={{ color: 'error.main', py: 2 }}>
      {/* <Typography fontWeight={600}>{t('common.error')}</Typography> */}
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        {t('components.dashboard.errorLoadingData')}
      </Typography>
    </Box>
  ) : null;

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Section Title */}
      <SectionTitleWithInfo
        titleKey={titleKey}
        descriptionKey={descriptionKey}
      />

      {/* Filter Controls and Export */}
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            gap: 2,
          }}
        >
          <MetricsFilterInputs
            value={params}
            onChange={setParams}
            onRefresh={() => refetch()}
            isRefreshing={isFetching}
            groupingsOptions={groupingsOptions}
            refreshLabelKey={refreshLabelKey}
          />
          {extraControls && extraControls(params, setParams)}
        </Box>

        <Box sx={{ mt: 2 }}>
          <ExportCsvButton
            metric={metricName}
            rows={data?.data}
            groupingKey={csvGroupingKey}
            intervalPeriod={params.intervalPeriod}
            startTime={params.startTime}
            endTime={params.endTime}
          />
        </Box>
      </Box>

      {/* Loading State */}
      {isLoading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State - error is unknown type but safe to check */}
      {errorUI}

      {/* Empty State */}
      {!isLoading && !error && data?.data?.length === 0 && (
        <Typography color="text.secondary" sx={{ py: 2 }}>
          {t('components.dashboard.noDataAvailable')}
        </Typography>
      )}

      {/* Data Display */}
      {!isLoading && !error && data?.data && data.data.length > 0 && (
        <>
          {renderData?.({
            data: data.data,
            params,
            metricValue,
            groupingKey,
          }) ?? (
            <Box
              sx={{
                mt: 2,
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(2, 1fr)',
                  lg: 'repeat(3, 1fr)',
                  xl: 'repeat(5, 1fr)',
                },
                gap: 2,
              }}
            >
              {data.data.map((item: MetricItemDTO) => (
                <MetricCard
                  key={item.id}
                  title={
                    item.dimensionLabels?.[groupingKey] ?? item.name ?? 'N/A'
                  }
                  value={metricValue(item.value)}
                  max={100}
                  suffix="%"
                />
              ))}
            </Box>
          )}
        </>
      )}
    </Paper>
  );
}
